import React, { useState, useEffect } from "react";
import logo from "../assets/logo_nobg.png";
import { useNavigate } from "react-router-dom";
import { collection, query, orderBy, onSnapshot, getCountFromServer } from "firebase/firestore";
import { db } from "../firebase";

export default function FinanceCommunities() {
  const navigate = useNavigate();
  const [communities, setCommunities] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "communities"), orderBy("createdAt", "asc"));
    return onSnapshot(q, async (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCommunities(docs);
      setLoading(false);
      const entries = await Promise.all(
        docs.map(async (c) => {
          try {
            const s = await getCountFromServer(collection(db, "communities", c.id, "subscribers"));
            return [c.id, s.data().count];
          } catch {
            return [c.id, 0];
          }
        })
      );
      setCounts(Object.fromEntries(entries));
    }, (err) => {
      console.error("Firestore communities error:", err.code, err.message);
      setLoading(false);
    });
  }, []);

  const totalMembers = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="fcs">
      <style>{CSS}</style>

      <nav className="fcs-nav">
        <div className="fcs-nav-inner">
          <a href="https://finance.shashankupadhyaya.com" className="fcs-logo">
            <img src={logo} alt="S/U" className="site-logo-img" />
            <span className="fcs-logo-label">Finance &amp; Markets</span>
          </a>
          <a href="https://finance.shashankupadhyaya.com" className="fcs-nav-back">← Finance Home</a>
        </div>
      </nav>

      <div className="fcs-page">
        <div className="fcs-header">
          <div className="fcs-header-inner">
            <span className="fcs-chip">Communities</span>
            <h1 className="fcs-h1">Finance Communities</h1>
            <p className="fcs-lead">Join a channel to get updates, vote on polls, and stay connected on markets and investing.</p>
            {!loading && communities.length > 0 && (
              <div className="fcs-stats-row">
                <div className="fcs-stat">
                  <span className="fcs-stat-val">{communities.length}</span>
                  <span className="fcs-stat-label">Channel{communities.length !== 1 ? "s" : ""}</span>
                </div>
                <div className="fcs-stat-div" />
                <div className="fcs-stat">
                  <span className="fcs-stat-val">{totalMembers}</span>
                  <span className="fcs-stat-label">Total Members</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="fcs-container">
          {loading ? (
            <div className="fcs-state">
              <div className="fcs-spinner" />
              <span>Loading communities…</span>
            </div>
          ) : communities.length === 0 ? (
            <div className="fcs-state">
              <span className="fcs-state-icon">◈</span>
              <p>No communities yet. Check back soon.</p>
            </div>
          ) : (
            <div className="fcs-grid">
              {communities.map((comm, idx) => {
                const memberCount = counts[comm.id];
                return (
                  <div
                    key={comm.id}
                    className="fcs-card"
                    onClick={() => navigate(`/community/${comm.slug}`)}
                    style={{ animationDelay: `${idx * 0.05}s` }}
                  >
                    <div className="fcs-card-avatar">
                      {comm.imageUrl
                        ? <img src={comm.imageUrl} alt="" onError={(e) => { e.target.style.display = "none"; }} />
                        : <span>{comm.emoji || "◈"}</span>}
                    </div>
                    <div className="fcs-card-body">
                      <h3 className="fcs-card-name">{comm.name}</h3>
                      {comm.description && <p className="fcs-card-desc">{comm.description}</p>}
                      <div className="fcs-member-badge">
                        <span className="fcs-dot" />
                        {memberCount !== undefined ? memberCount.toLocaleString() : "…"} member{memberCount !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <span className="fcs-card-cta">Join →</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <footer className="fcs-footer">
          <div className="fcs-container">
            <span>© 2026 Shashank Upadhyaya</span>
            <a href="https://shashankupadhyaya.com">shashankupadhyaya.com ↗</a>
          </div>
        </footer>
      </div>
    </div>
  );
}

const CSS = `
  .fcs {
    --bg: #0A0B0D; --card: #0E1116; --border: rgba(255,255,255,0.08);
    --text: #EDEFF2; --muted: #9AA3AF; --gold: #C7F94B;
    --gold-dim: rgba(199,249,75,0.12); --nav-h: 64px; --radius: 8px;
    background: var(--bg); color: var(--text); min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    font-size: 15px; -webkit-font-smoothing: antialiased;
  }
  .fcs *, .fcs *::before, .fcs *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .fcs a { color: inherit; text-decoration: none; }

  .fcs-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 50;
    height: var(--nav-h); background: rgba(10,11,13,0.95);
    backdrop-filter: blur(20px); border-bottom: 1px solid var(--border);
  }
  .fcs-nav-inner { max-width: 900px; margin: 0 auto; padding: 0 28px; height: 100%; display: flex; align-items: center; }
  .fcs-logo { display: flex; align-items: center; gap: 10px; }
  .site-logo-img { height: 56px; width: auto; display: block; object-fit: contain; }
  .fcs-logo-label { font-size: 13px; color: var(--muted); padding-left: 10px; border-left: 1px solid var(--border); }
  .fcs-nav-back { font-size: 13px; color: var(--muted); margin-left: auto; transition: color 0.15s; }
  .fcs-nav-back:hover { color: var(--gold); }

  .fcs-page { padding-top: var(--nav-h); }
  .fcs-header { border-bottom: 1px solid var(--border); padding: 60px 0 44px; }
  .fcs-header-inner { max-width: 900px; margin: 0 auto; padding: 0 28px; }
  .fcs-chip {
    display: inline-block; font-size: 11px; font-weight: 600; letter-spacing: 0.08em;
    text-transform: uppercase; color: var(--gold); background: var(--gold-dim);
    border: 1px solid rgba(199,249,75,0.25); padding: 4px 10px; border-radius: 4px; margin-bottom: 18px;
  }
  .fcs-h1 {
    font-family: 'Archivo', sans-serif; font-weight: 900;
    font-size: clamp(28px, 4.5vw, 48px); letter-spacing: -0.03em; line-height: 1.05; margin-bottom: 12px;
  }
  .fcs-lead { font-size: 15px; color: var(--muted); max-width: 520px; line-height: 1.7; margin-bottom: 24px; }
  .fcs-stats-row { display: flex; align-items: center; gap: 20px; }
  .fcs-stat { display: flex; flex-direction: column; gap: 2px; }
  .fcs-stat-val { font-family: 'Archivo', sans-serif; font-weight: 800; font-size: 22px; }
  .fcs-stat-label { font-size: 12px; color: var(--muted); }
  .fcs-stat-div { width: 1px; height: 32px; background: var(--border); }

  .fcs-container { max-width: 900px; margin: 0 auto; padding: 0 28px; }
  .fcs-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; padding: 40px 0; }
  .fcs-card {
    background: var(--card); border: 1px solid var(--border); border-radius: var(--radius);
    padding: 24px; display: flex; align-items: center; gap: 16px;
    cursor: pointer; transition: border-color 0.2s, transform 0.2s;
    animation: fcs-up 0.35s ease both;
  }
  .fcs-card:hover { border-color: rgba(199,249,75,0.4); transform: translateY(-2px); }
  @keyframes fcs-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
  .fcs-card-avatar {
    width: 52px; height: 52px; border-radius: 12px; background: var(--gold-dim);
    border: 1px solid rgba(199,249,75,0.2); display: flex; align-items: center; justify-content: center;
    font-size: 22px; flex-shrink: 0; overflow: hidden; color: var(--gold);
  }
  .fcs-card-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .fcs-card-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
  .fcs-card-name { font-family: 'Archivo', sans-serif; font-weight: 700; font-size: 16px; }
  .fcs-card-desc { font-size: 13px; color: var(--muted); line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .fcs-member-badge { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--muted); margin-top: 4px; }
  .fcs-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--gold); flex-shrink: 0; }
  .fcs-card-cta { font-size: 14px; font-weight: 600; color: var(--gold); flex-shrink: 0; }

  .fcs-state { text-align: center; padding: 80px 0; display: flex; flex-direction: column; align-items: center; gap: 14px; font-size: 14px; color: var(--muted); }
  .fcs-state-icon { font-size: 32px; }
  .fcs-spinner { width: 28px; height: 28px; border-radius: 50%; border: 2px solid var(--border); border-top-color: var(--gold); animation: fcs-spin 0.75s linear infinite; }
  @keyframes fcs-spin { to { transform: rotate(360deg); } }

  .fcs-footer { margin-top: 60px; border-top: 1px solid var(--border); padding: 28px 0; }
  .fcs-footer .fcs-container { display: flex; align-items: center; justify-content: space-between; font-size: 13px; color: var(--muted); }
  .fcs-footer a { transition: color 0.15s; }
  .fcs-footer a:hover { color: var(--gold); }

  @media (max-width: 640px) {
    .fcs-grid { grid-template-columns: 1fr; }
    .fcs-h1 { font-size: 26px; }
    .fcs-container, .fcs-header-inner, .fcs-nav-inner { padding-left: 16px; padding-right: 16px; }
    .fcs-header { padding: 36px 0 28px; }
    .fcs-card { padding: 16px; }
    .fcs-footer .fcs-container { flex-wrap: wrap; gap: 8px; }
  }
  @media (max-width: 480px) {
    .site-logo-img { height: 36px; }
    .fcs-logo-label { display: none; }
  }
`;
